import { useEffect, useMemo, useState } from "react";
import { PlusIcon } from "lucide-react";

import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import { Button, SortDropdownValue } from "@/modules/common/components";
import { useFilters } from "@/modules/common/filters";
import { FILTER_KEYS } from "@/modules/common/filters/keys";
import { usePagination } from "@/modules/common/hooks/usePagination";
import { LeadSortField } from "@/modules/common/types/Leads.enum";
import { AdminUserRole } from "@/modules/common/types/Users.enum";
import { ListAdminLeadsDto } from "@/service/dto/admin-leads";

import LeadFormModal from "./components/LeadFormModal";
import LeadsFilterBar from "./components/LeadsFilterBar";
import LeadsList from "./components/LeadsList";
import LeadsTotalizers from "./components/LeadsTotalizers";
import { LEAD_FILTER_FIELDS, LeadFilters } from "./constants/LeadFilterFields";
import useLeadsList from "./hooks/useLeadsList";
import useLeadsSummary from "./hooks/useLeadsSummary";

const Leads = () => {
  const { canUserSee } = useSessionStore();
  const canWrite = canUserSee(AdminUserRole.ADMIN);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [sort, setSort] = useState<SortDropdownValue<LeadSortField> | null>(null);

  const { data: summary, isLoading: isSummaryLoading } = useLeadsSummary();

  const filters = useFilters<LeadFilters>({
    fields: LEAD_FILTER_FIELDS,
    filterKey: FILTER_KEYS.LEADS,
  });

  const { page, setPage, limit, setPageSize } = usePagination();

  const filtersKey = JSON.stringify(filters.queryParams);
  const sortKey = sort ? `${sort.field}:${sort.order}` : "";
  useEffect(() => {
    setPage(1);
  }, [filtersKey, sortKey]);

  const params = useMemo<ListAdminLeadsDto>(() => {
    const { status, origin, segment, ...rest } = filters.queryParams;

    return {
      ...rest,
      statuses: status,
      origins: origin,
      segments: segment ? [segment] : undefined,
      sortBy: sort?.field,
      sortOrder: sort?.order,
      page,
      limit,
    };
  }, [filters.queryParams, sort, page, limit]);

  const { data: leads, isLoading: isLeadsLoading, isFetching: isLeadsFetching } = useLeadsList(params);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col">
          <p className="text-[22px] font-bold text-ui-primary">Leads</p>
          <p className="text-[14px] text-ui-text-muted">Gerencie os leads recebidos pelos canais comerciais da POUPenergia</p>
        </div>

        {canWrite && <Button label="Novo lead" icon={<PlusIcon />} className="w-full sm:w-auto" onClick={() => setIsCreateOpen(true)} />}
      </div>

      <LeadsTotalizers data={summary} isLoading={isSummaryLoading} />

      <LeadsFilterBar filters={filters} sort={sort} onSortChange={setSort} />

      <LeadsList
        data={leads}
        limit={limit}
        isLoading={isLeadsLoading}
        isFetching={isLeadsFetching}
        onChangePage={setPage}
        onChangePageSize={setPageSize}
      />

      <LeadFormModal open={isCreateOpen} onOpenChange={setIsCreateOpen} />
    </div>
  );
};

export default Leads;
